/**
 * Sprint 1b-3 — issuance visibility.
 *
 * After Sprint 1, issuance moved to Complete, so an assessment can sit with its
 * differential CONFIRMED and its report NOT ISSUED. In a list that state was
 * indistinguishable from an untouched draft, and nothing told the front desk
 * which records still needed finishing (adversarial item 1: "close the tab
 * after step 7, believing the report went out").
 *
 * Pure and import-free so it can be unit-tested in the node test environment.
 * Derived entirely from fields the list endpoint already returns; no schema
 * change and no new state is persisted anywhere.
 */

export type LifecycleState =
  | 'draft'                // started, nothing confirmed yet
  | 'confirmed-not-issued' // differential confirmed, Complete not pressed
  | 'amending'             // issued, reopened by its author
  | 'issued';              // issued; carries a version

export interface LifecycleInput {
  status: string;
  /** confirmed + clinician-added hypotheses */
  confirmedHypotheses?: number | null;
  /** highest locked report version, null when never issued */
  reportVersion?: number | null;
}

export function lifecycleState(a: LifecycleInput): LifecycleState {
  if (a.status === 'amending') return 'amending';
  if (a.status === 'finalized' || a.status === 'completed') return 'issued';
  return (a.confirmedHypotheses ?? 0) > 0 ? 'confirmed-not-issued' : 'draft';
}

export interface LifecycleLabel {
  /** i18n key */
  key: string;
  /** English fallback, so a missing translation still reads correctly */
  fallback: string;
  /** interpolation params, when the label carries a version */
  params?: Record<string, string | number>;
  /** stable hook for styling and for tests */
  tone: 'neutral' | 'attention' | 'done';
}

export function lifecycleLabel(a: LifecycleInput): LifecycleLabel {
  switch (lifecycleState(a)) {
    case 'issued': {
      const v = a.reportVersion ?? 1;
      return {
        key: 'lifecycle.issued',
        fallback: 'Issued v{{version}}',
        params: { version: v },
        tone: 'done',
      };
    }
    case 'amending':
      return { key: 'lifecycle.amending', fallback: 'Amendment in progress', tone: 'attention' };
    case 'confirmed-not-issued':
      return { key: 'lifecycle.confirmedNotIssued', fallback: 'Confirmed, not issued', tone: 'attention' };
    default:
      return { key: 'lifecycle.draft', fallback: 'Draft', tone: 'neutral' };
  }
}
